import './styles/Login.scss'
import React, {useState, useContext } from "react";
import { useNavigate } from 'react-router-dom';
import { AccountContext } from './Accounts.js'
import Pool from './UserPool.js';
import { CognitoUser } from 'amazon-cognito-identity-js';
import axios from 'axios';
import { useUserContext } from './contexts/UserContext.js';
import Spinner from './components/Spinner.js';
import { WarningCircleIcon, CheckCircleIcon, XCircleIcon } from '@phosphor-icons/react';

export default function Login({ setPage, setUser }) {
    const {
        setConfig
    } = useUserContext();

    const [error, setError] = useState(null);
    const [errorType, setErrorType] = useState('error'); // 'error', 'warning', 'success'
    const [stage, setStage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showSubscriptionButton, setShowSubscriptionButton] = useState(false);
    const [cognitoUserId, setCognitoUserId] = useState('');

    const navigate = useNavigate();
    const { authenticate } = useContext(AccountContext);

    // Helper function to display errors with different types
    const displayError = (message, type = 'error') => {
        setError(message);
        setErrorType(type);
        setLoading(false);
    };

    // Clear error when user starts typing or changes stage
    const clearError = () => {
        if (error) {
            setError(null);
            setErrorType('error');
            setShowSubscriptionButton(false);
        }
    };

    // Helper function to render the appropriate icon for error type
    const renderErrorIcon = (type) => {
        switch (type) {
            case 'error':
                return <XCircleIcon size={24} weight="bold" />;
            case 'warning':
                return <WarningCircleIcon size={24} weight="bold" />;
            case 'success':
                return <CheckCircleIcon size={24} weight="bold" />;
            default:
                return <WarningCircleIcon size={24} weight="bold" />;
        }
    };

    const logIn = async (event) => {
        setLoading(true);
        clearError(); // Clear any existing errors
        event.preventDefault();
        try {
            if(event.target.email.value === '' || event.target.password.value === '') {
                displayError('Please provide both email and password', 'warning');
                return;
            }
            else {
                authenticate(event.target.email.value, event.target.password.value)
                    .then((data) => {
                        console.log(data.idToken.jwtToken)
                        axios.get('https://firewatch-api.space-eyes.com/user/settings', {
                            headers: {
                                'Authorization': `Bearer ${data.idToken.jwtToken}`
                            }
                        }).then((response) => {
                            const settingsReponse = response.data.settings;

                            axios.get('https://firewatch-api.space-eyes.com/user/signin?platform=web', {
                                headers: {
                                    'Authorization': `Bearer ${data.idToken.jwtToken}`
                                }
                            }).then((res) => {
                                const subscriptionResponse = res.data.subscription[0];

                                if (subscriptionResponse.subscription_type !== 'inactive') {
                                    setUser(data);
                                    setConfig({
                                        ...settingsReponse,
                                        subscription_type: subscriptionResponse.subscription_type,
                                        customerId: subscriptionResponse.customer_id
                                    });
                                    navigate('/home');
                                    setLoading(false);
                                } else if (subscriptionResponse.message && subscriptionResponse.message.trim() !== '') {
                                    displayError(subscriptionResponse.message, 'warning');
                                    setCognitoUserId(data.idToken.payload.sub);
                                    setShowSubscriptionButton(true);
                                } else {
                                    displayError('Our records show that you have a FireWatch account, but do not have an active paying subscription.', 'warning');
                                    setCognitoUserId(data.idToken.payload.sub);
                                    setShowSubscriptionButton(true);
                                }
                            }).catch((err) => {
                                console.log(err);
                                if (err.response?.status === 401) {
                                    displayError('Authentication failed. Please check your credentials and try again.', 'error');
                                } else if (err.response?.status === 403) {
                                    displayError('Access denied. Please contact support if you believe this is an error.', 'error');
                                } else if (err.response?.status >= 500) {
                                    displayError('Server error. Please try again in a few moments.', 'error');
                                } else {
                                    displayError('An unexpected error occurred. Please try again.', 'error');
                                }
                            })
                        }).catch((err) => {
                            console.log(err);
                            if (err.response?.status === 401) {
                                displayError('Authentication failed. Please check your credentials and try again.', 'error');
                            } else if (err.response?.status === 403) {
                                displayError('Access denied. Please contact support if you believe this is an error.', 'error');
                            } else if (err.response?.status >= 500) {
                                displayError('Server error. Please try again in a few moments.', 'error');
                            } else {
                                displayError('An unexpected error occurred. Please try again.', 'error');
                            }
                        })
                    })
                    .catch((err) => {
                        console.log(err);
                        if (err.code === 'NotAuthorizedException') {
                            displayError('Invalid email or password. Please check your credentials and try again.', 'error');
                        } else if (err.code === 'UserNotConfirmedException') {
                            displayError('Please verify your email address before logging in. Check your inbox for a verification email.', 'warning');
                        } else if (err.code === 'UserNotFoundException') {
                            displayError('No account found with this email address. Please check your email or sign up for a new account.', 'error');
                        } else if (err.code === 'TooManyRequestsException') {
                            displayError('Too many login attempts. Please wait a few minutes before trying again.', 'warning');
                        } else {
                            displayError('Login failed. Please check your credentials and try again.', 'error');
                        }
                    });
            }
        } catch (err) {
            console.log(err);
            displayError('An unexpected error occurred. Please try again.', 'error');
        }
    };

    const sendCode = event => {
        event.preventDefault();
        clearError();
      
        if (!email) {
            displayError('Please enter your email address', 'warning');
            return;
        }
      
        const cognitoUser = new CognitoUser({ Username: email, Pool });
        // setUser(cognitoUser);
      
        cognitoUser.forgotPassword({
          onSuccess: data => {
            console.log('Code sent:', data);
            displayError('Reset code sent to your email address', 'success');
          },
          onFailure: err  => {
            console.error('Failed to send code', err);
            if (err.code === 'UserNotFoundException') {
                displayError('No account found with this email address. Please check your email or sign up for a new account.', 'error');
            } else if (err.code === 'LimitExceededException') {
                displayError('Too many reset attempts. Please wait before trying again.', 'warning');
            } else {
                displayError(err.message || 'Error sending reset code. Please try again.', 'error');
            }
          },
          inputVerificationCode: () => {
            // this fires once AWS has dispatched the code email
            setStage(2);
          }
        });
      };
      

    const resetPassword = (event) => {
        event.preventDefault();
        clearError();
      
        if (!verificationCode) {
            displayError('Please enter the verification code', 'warning');
            return;
        }
        
        if (!password) {
            displayError('Please enter a new password', 'warning');
            return;
        }
        
        if (password.length < 8) {
            displayError('Password must be at least 8 characters long', 'warning');
            return;
        }
      
        if (password !== confirmPassword) {
            displayError('Passwords do not match. Please try again.', 'warning');
            return;
        }
      
        const cognitoUser = new CognitoUser({ Username: email, Pool });
        
        cognitoUser.confirmPassword(
          verificationCode,
          password,
          {
            onSuccess: () => {
              console.log('Password reset success');
              displayError('Password reset successfully! You can now log in with your new password.', 'success');
              setStage(3);
            },
            onFailure: err => {
              console.error('Reset failed', err);
              if (err.code === 'CodeMismatchException') {
                displayError('Invalid verification code. Please check the code and try again.', 'error');
              } else if (err.code === 'ExpiredCodeException') {
                displayError('Verification code has expired. Please request a new code.', 'error');
              } else if (err.code === 'InvalidPasswordException') {
                displayError('Password does not meet requirements. Please use a stronger password.', 'warning');
              } else {
                displayError(err.message || 'Password reset failed. Please try again.', 'error');
              }
            }
          }
        );
      };      

    const stageSwitch = () => {
        switch (stage) {
            case 0: return <div className='login-right'>
                <form className='login-right-container' onSubmit={logIn}>
                    <div className='login-right-container-title'>Log in</div>
                    {error && <div className={`login-right-container-error login-right-container-error-${errorType}`}>
                        {renderErrorIcon(errorType)}
                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem'}}>
                            <span>{error}</span>
                            {showSubscriptionButton && (
                                <button type='button' style={{
                                    background: 'transparent',
                                    border: '1px solid currentColor',
                                    color: 'lightgray',
                                    fontSize: '0.875rem',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '0.5rem',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    transition: 'all 0.3s ease'
                                }} onClick={() => {
                                    sessionStorage.setItem('goToSubscriptions', 'true');
                                    sessionStorage.setItem('existingCognitoUserId', cognitoUserId);
                                    setPage(1);
                                }}>
                                    View Subscription Plans
                                </button>
                            )}
                        </div>
                    </div>}
                    <div className='login-right-container-subtitle'>Email address</div>
                    <div className='login-right-container-field'>
                        <div className='login-right-container-field-email'/>
                        <input name='email' className='login-right-container-field-input' placeholder='test@email.com' onChange={clearError}/>
                    </div>
                    <div className='login-right-container-subtitle'>Password</div>
                    <div className='login-right-container-field'>
                        <div className='login-right-container-field-lock'/>
                        <input name='password' type={showPassword ? 'text' : 'password'} className='login-right-container-field-input' placeholder='password' onChange={clearError}/>
                        <div onClick={() => setShowPassword((prevState) => !prevState)} className={showPassword ? 'login-right-container-field-show-icon' : 'login-right-container-field-hide-icon'}/>
                    </div>
                    {loading ?
                    <div style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
                        <Spinner />
                    </div>
                    :
                    <button type='submit' className='login-right-container-button'>Sign in</button>}
                    <div className='login-right-container-text'>Forgot password? Click <b className='login-right-container-link' onClick={() => setStage(1)}>here</b> to reset.</div>
                    <div className='login-right-container-text'>Don't have an account? Click <b className='login-right-container-link' onClick={() => setPage(1)}>here</b> to sign up.</div>
                    <div className='login-right-container-text'>Space-Eyes, Inc. is a US based company and only conducts business with US allies.</div>
                </form>
                <div className='login-right-logo'/>
            </div>
            case 1: return <div className='login-right'>
                <form className='login-right-container' onSubmit={sendCode}>
                    <div className='login-right-container-title'>Forgot password</div>
                    {error && <div className={`login-right-container-error login-right-container-error-${errorType}`}>
                        {renderErrorIcon(errorType)}
                        <span>{error}</span>
                    </div>}
                    <div className='login-right-container-subtitle'>Email address</div>
                    <div className='login-right-container-field'>
                        <div className='login-right-container-field-email'/>
                        <input name='email' className='login-right-container-field-input' placeholder='test@email.com' onChange={(e) => {setEmail(e.target.value); clearError();}}/>
                    </div>
                    <button type='submit' className='login-right-container-button'>Continue</button>
                    <div className='login-right-container-text'><b className='login-right-container-link' onClick={() => setStage(0)}>Return</b></div>
                </form>
                <div className='login-right-logo'/>
            </div>
            case 2: return <div className='login-right'>
                <form className='login-right-container' onSubmit={resetPassword} autoComplete='off'>
                    <div className='login-right-container-title'>Forgot password</div>
                    {error && <div className={`login-right-container-error login-right-container-error-${errorType}`}>
                        {renderErrorIcon(errorType)}
                        <span>{error}</span>
                    </div>}
                    <div className='login-right-container-subs-success-subtext' style={{marginTop: '-1rem', textAlign: 'left'}}>A reset code has been sent to your email.</div>
                    <div className='login-right-container-subtitle'>6-digit code</div>
                    <div className='login-right-container-field'>
                        <div className='login-right-container-field-code'/>
                        <input value={verificationCode} name='code' className='login-right-container-field-input' onChange={(e) => {setVerificationCode(e.target.value); clearError();}}/>
                    </div>
                    <div className='login-right-container-subtitle'>New password</div>
                    <div className='login-right-container-field'>
                        <div className='login-right-container-field-lock'/>
                        <input value={password} onChange={(e) => {setPassword(e.target.value); clearError();}} name='newPassword' type={showPassword ? 'text' : 'password'} className='login-right-container-field-input'/>
                        <div onClick={() => setShowPassword((prevState) => !prevState)} className={showPassword ? 'login-right-container-field-show-icon' : 'login-right-container-field-hide-icon'}/>
                    </div>
                    <div className='login-right-container-subtitle'>Confirm new password</div>
                    <div className='login-right-container-field'>
                        <div className='login-right-container-field-lock'/>
                        <input value={confirmPassword} onChange={(e) => {setConfirmPassword(e.target.value); clearError();}} name='confirmedNewPassword' type={showPassword ? 'text' : 'password'} className='login-right-container-field-input' placeholder='confirm new password'/>
                        <div onClick={() => setShowPassword((prevState) => !prevState)} className={showPassword ? 'login-right-container-field-show-icon' : 'login-right-container-field-hide-icon'}/>
                    </div>
                    <button type='submit' className='login-right-container-button'>Reset password</button>
                    <div className='login-right-container-text'><b className='login-right-container-link' onClick={() => setStage(1)}>Return</b></div>
                </form>
                <div className='login-right-logo'/>
            </div>
            case 3: return <div className='login-right'>
                <div className='login-right-container'>
                    <div className='login-right-container-title'>Forgot password</div>
                    <div className='login-right-container-subs-success-subtext' style={{marginTop: '-1rem', textAlign: 'left'}}>Password reset successfully.</div>
                    <button className='login-right-container-button' onClick={() => setStage(0)}>Log in</button>
                </div>
                <div className='login-right-logo'/>
            </div>
        } 
    }

    return (stageSwitch());
}